import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Check, ChevronsUpDown, Globe } from "lucide-react";

// Curated common US / Canada zones shown at the top.
const CURATED = [
  { v: "America/New_York", l: "Eastern — New York (ET)" },
  { v: "America/Chicago", l: "Central — Chicago (CT)" },
  { v: "America/Denver", l: "Mountain — Denver (MT)" },
  { v: "America/Phoenix", l: "Arizona — Phoenix (MST, no DST)" },
  { v: "America/Los_Angeles", l: "Pacific — Los Angeles (PT)" },
  { v: "America/Anchorage", l: "Alaska — Anchorage (AKT)" },
  { v: "Pacific/Honolulu", l: "Hawaii — Honolulu (HST)" },
  { v: "America/Toronto", l: "Eastern — Toronto (Canada)" },
  { v: "America/Winnipeg", l: "Central — Winnipeg (Canada)" },
  { v: "America/Edmonton", l: "Mountain — Edmonton (Canada)" },
  { v: "America/Vancouver", l: "Pacific — Vancouver (Canada)" },
  { v: "America/Halifax", l: "Atlantic — Halifax (Canada)" },
  { v: "America/St_Johns", l: "Newfoundland — St. John's (Canada)" },
];

function allZones() {
  try {
    if (typeof Intl.supportedValuesOf === "function") return Intl.supportedValuesOf("timeZone");
  } catch (e) { /* noop */ }
  return CURATED.map((c) => c.v);
}

// Current UTC offset label, e.g. "GMT-7", for a given IANA zone.
function offsetLabel(tz) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "shortOffset" }).formatToParts(new Date());
    const p = parts.find((x) => x.type === "timeZoneName");
    return p ? p.value : "";
  } catch (e) { return ""; }
}

export function TimezoneSelect({ value, onChange, "data-testid": testId = "settings-timezone-select" }) {
  const [open, setOpen] = useState(false);
  const curatedSet = useMemo(() => new Set(CURATED.map((c) => c.v)), []);
  const others = useMemo(() => allZones().filter((z) => !curatedSet.has(z)).sort(), [curatedSet]);

  const label = useMemo(() => {
    if (!value) return "Select time zone…";
    const c = CURATED.find((x) => x.v === value);
    const off = offsetLabel(value);
    return `${c ? c.l : value}${off ? `  ·  ${off}` : ""}`;
  }, [value]);

  const pick = (v) => { onChange(v); setOpen(false); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open}
          className="w-full justify-between rounded-none mt-1 font-normal" data-testid={testId}>
          <span className="flex items-center gap-2 truncate"><Globe className="h-4 w-4 shrink-0 text-muted-foreground" /><span className="truncate">{label}</span></span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 rounded-none w-[--radix-popover-trigger-width] min-w-[320px]" align="start">
        <Command>
          <CommandInput placeholder="Search time zones…" data-testid="settings-timezone-search" />
          <CommandList className="max-h-72">
            <CommandEmpty>No time zone found.</CommandEmpty>
            <CommandGroup heading="Common (US / Canada)">
              {CURATED.map((c) => (
                <CommandItem key={c.v} value={`${c.l} ${c.v}`} onSelect={() => pick(c.v)} data-testid={`tz-opt-${c.v}`}>
                  <Check className={`mr-2 h-4 w-4 ${value === c.v ? "opacity-100" : "opacity-0"}`} />
                  <span className="flex-1">{c.l}</span>
                  <span className="text-xs text-muted-foreground ml-2">{offsetLabel(c.v)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandGroup heading="All time zones">
              {others.map((z) => (
                <CommandItem key={z} value={z} onSelect={() => pick(z)} data-testid={`tz-opt-${z}`}>
                  <Check className={`mr-2 h-4 w-4 ${value === z ? "opacity-100" : "opacity-0"}`} />
                  <span className="flex-1">{z.replace(/_/g, " ")}</span>
                  <span className="text-xs text-muted-foreground ml-2">{offsetLabel(z)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
